export const AGENDA_SECTION_DEFINITIONS = [
  { key: "upcoming", label: "Prossimi" },
  { key: "closing", label: "Da chiudere" },
  { key: "completed", label: "Completati" },
  { key: "cancelled", label: "Annullati" },
  { key: "no_show", label: "No-show" },
];

export const DEFAULT_BUSINESS_TIMEZONE = "Europe/Rome";

const toTimeZoneDateKey = (value, timeZone = DEFAULT_BUSINESS_TIMEZONE) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

export const toBusinessDateKey = (value, timeZone = DEFAULT_BUSINESS_TIMEZONE) =>
  toTimeZoneDateKey(value, timeZone) || "unscheduled";

const formatDuration = (minutes) => {
  const totalMinutes = Math.max(0, Math.round(minutes));
  const hours = Math.floor(totalMinutes / 60);
  const remainder = totalMinutes % 60;
  if (hours && remainder) return `${hours} h ${remainder} min`;
  if (hours) return `${hours} h`;
  return `${remainder} min`;
};

export const classifyAppointment = (appointment, now = new Date()) => {
  const status = appointment?.status;
  if (status === "completed") return "completed";
  if (status === "cancelled") return "cancelled";
  if (status === "no_show") return "no_show";

  const startTime = appointment?.start_time ? Date.parse(appointment.start_time) : Number.NaN;
  if (status === "scheduled" && Number.isFinite(startTime) && startTime < now.getTime()) {
    return "closing";
  }
  return "upcoming";
};

export const isAgendaAppointmentActionable = (appointment, now = new Date()) => {
  const section = classifyAppointment(appointment, now);
  return section === "upcoming" || section === "closing";
};

export const buildAgendaSections = (appointments, now = new Date()) => {
  const sections = Object.fromEntries(
    AGENDA_SECTION_DEFINITIONS.map(({ key }) => [key, []])
  );
  (appointments || []).forEach((appointment) => {
    sections[classifyAppointment(appointment, now)].push(appointment);
  });
  return sections;
};

export const toLocalDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "unscheduled";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const dateFromKey = (dateKey) => {
  const [year, month, day] = String(dateKey).split("-").map(Number);
  return new Date(year, month - 1, day, 12);
};

export const shiftDateKey = (dateKey, dayDelta) => {
  const date = dateFromKey(dateKey);
  date.setDate(date.getDate() + dayDelta);
  return toLocalDateKey(date);
};

export const getAgendaWeekStartKey = (dateKey) => {
  const date = dateFromKey(dateKey);
  const mondayOffset = (date.getDay() + 6) % 7;
  return shiftDateKey(dateKey, -mondayOffset);
};

export const shiftWeekKey = (dateKey, weekDelta) =>
  shiftDateKey(dateKey, weekDelta * 7);

export const formatAgendaDayLabel = (dateKey) => {
  if (dateKey === "unscheduled") return "Data da definire";
  const label = new Intl.DateTimeFormat("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(dateFromKey(dateKey));
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const buildAgendaWeekDays = (
  selectedDateKey,
  appointments,
  timeZone = DEFAULT_BUSINESS_TIMEZONE,
  now = new Date()
) => {
  const mondayKey = getAgendaWeekStartKey(selectedDateKey);
  const counts = (appointments || []).filter(
    (appointment) => isAgendaAppointmentActionable(appointment, now)
  ).reduce((result, appointment) => {
    const dateKey = toBusinessDateKey(appointment?.start_time, timeZone);
    result[dateKey] = (result[dateKey] || 0) + 1;
    return result;
  }, {});
  const shortDayFormatter = new Intl.DateTimeFormat("it-IT", { weekday: "short" });
  const dayNumberFormatter = new Intl.DateTimeFormat("it-IT", { day: "numeric" });

  return Array.from({ length: 7 }, (_, index) => {
    const dateKey = shiftDateKey(mondayKey, index);
    return {
      dateKey,
      dayLabel: shortDayFormatter.format(dateFromKey(dateKey)).replace(".", ""),
      dateLabel: dayNumberFormatter.format(dateFromKey(dateKey)).replace(".", ""),
      count: counts[dateKey] || 0,
      selected: dateKey === selectedDateKey,
    };
  });
};

export const formatAgendaWeekLabel = (selectedDateKey) => {
  const start = dateFromKey(getAgendaWeekStartKey(selectedDateKey));
  const end = dateFromKey(shiftDateKey(toLocalDateKey(start), 6));
  const month = (date) => new Intl.DateTimeFormat("it-IT", { month: "long" }).format(date);

  if (start.getFullYear() !== end.getFullYear()) {
    return `${start.getDate()} ${month(start)} ${start.getFullYear()} – ${end.getDate()} ${month(end)} ${end.getFullYear()}`;
  }
  if (start.getMonth() !== end.getMonth()) {
    return `${start.getDate()} ${month(start)} – ${end.getDate()} ${month(end)} ${end.getFullYear()}`;
  }
  return `${start.getDate()}–${end.getDate()} ${month(end)} ${end.getFullYear()}`;
};

export const formatAgendaWeekContextLabel = (selectedDateKey) => {
  const start = dateFromKey(getAgendaWeekStartKey(selectedDateKey));
  const end = dateFromKey(shiftDateKey(toLocalDateKey(start), 6));
  const capitalize = (label) => label.charAt(0).toUpperCase() + label.slice(1);
  const month = (date) => new Intl.DateTimeFormat("it-IT", { month: "long" }).format(date);

  if (start.getFullYear() !== end.getFullYear()) {
    return `${capitalize(month(start))} ${start.getFullYear()} / ${capitalize(month(end))} ${end.getFullYear()}`;
  }
  if (start.getMonth() !== end.getMonth()) {
    return `${capitalize(month(start))} / ${capitalize(month(end))} ${end.getFullYear()}`;
  }
  return `${capitalize(month(end))} ${end.getFullYear()}`;
};

export const shiftMonthKey = (dateKey, monthDelta) => {
  const date = dateFromKey(dateKey);
  return toLocalDateKey(new Date(date.getFullYear(), date.getMonth() + monthDelta, 1, 12));
};

export const formatAgendaMonthLabel = (dateKey) => {
  const label = new Intl.DateTimeFormat("it-IT", { month: "long", year: "numeric" })
    .format(dateFromKey(dateKey));
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const getAgendaMonthGridRange = (selectedDateKey) => {
  const selectedDate = dateFromKey(selectedDateKey);
  const monthStartKey = toLocalDateKey(
    new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1, 12)
  );
  const mondayOffset = (dateFromKey(monthStartKey).getDay() + 6) % 7;
  const startDateKey = shiftDateKey(monthStartKey, -mondayOffset);
  return {
    startDateKey,
    endDateKey: shiftDateKey(startDateKey, 41),
  };
};

export const buildAgendaMonthDays = (
  selectedDateKey,
  appointments,
  timeZone = DEFAULT_BUSINESS_TIMEZONE,
  now = new Date()
) => {
  const selectedDate = dateFromKey(selectedDateKey);
  const { startDateKey: gridStartKey } = getAgendaMonthGridRange(selectedDateKey);
  const counts = (appointments || []).filter(
    (appointment) => isAgendaAppointmentActionable(appointment, now)
  ).reduce((result, appointment) => {
    const dateKey = toBusinessDateKey(appointment?.start_time, timeZone);
    result[dateKey] = (result[dateKey] || 0) + 1;
    return result;
  }, {});

  return Array.from({ length: 42 }, (_, index) => {
    const dateKey = shiftDateKey(gridStartKey, index);
    const date = dateFromKey(dateKey);
    return {
      dateKey,
      dayNumber: date.getDate(),
      count: counts[dateKey] || 0,
      inCurrentMonth: date.getMonth() === selectedDate.getMonth(),
      selected: dateKey === selectedDateKey,
    };
  });
};

export const buildAgendaRows = (
  appointments,
  timeZone = DEFAULT_BUSINESS_TIMEZONE
) => {
  const rows = [];
  let currentDateKey = null;
  (appointments || []).forEach((appointment) => {
    const dateKey = toBusinessDateKey(appointment?.start_time, timeZone);
    if (dateKey !== currentDateKey) {
      rows.push({ type: "day", dateKey, label: formatAgendaDayLabel(dateKey) });
      currentDateKey = dateKey;
    }
    rows.push({ type: "appointment", appointment });
  });
  return rows;
};

export const buildAgendaDaySummary = (appointments, now = new Date()) => {
  const sections = buildAgendaSections(appointments, now);
  const total = (appointments || []).length;
  const statusParts = [
    [sections.completed.length, "svolto", "svolti"],
    [sections.cancelled.length, "annullato", "annullati"],
    [sections.no_show.length, "no-show", "no-show"],
    [sections.closing.length, "da chiudere", "da chiudere"],
  ]
    .filter(([count]) => count > 0)
    .map(([count, singular, plural]) => `${count} ${count === 1 ? singular : plural}`);

  return {
    total,
    totalLabel: `${total} ${total === 1 ? "appuntamento" : "appuntamenti"}`,
    statusLabel: statusParts.join(" · "),
    sections,
  };
};

export const getNextClientState = (
  appointments,
  now = new Date(),
  timeZone = DEFAULT_BUSINESS_TIMEZONE
) => {
  const nowMs = now.getTime();
  const todayKey = toTimeZoneDateKey(now, timeZone);
  const scheduled = (appointments || [])
    .filter((appointment) => appointment?.status === "scheduled")
    .map((appointment) => ({
      appointment,
      startMs: Date.parse(appointment.start_time),
      endMs: Date.parse(appointment.end_time),
    }))
    .filter((item) => Number.isFinite(item.startMs))
    .sort((left, right) => left.startMs - right.startMs);

  const current = scheduled.find(
    (item) =>
      item.startMs <= nowMs &&
      Number.isFinite(item.endMs) &&
      item.endMs > nowMs &&
      toTimeZoneDateKey(item.appointment.start_time, timeZone) === todayKey
  );
  if (current) {
    const elapsedMinutes = Math.max(0, Math.floor((nowMs - current.startMs) / 60000));
    return {
      kind: "current",
      appointment: current.appointment,
      minutesUntil: 0,
      timingLabel: elapsedMinutes < 1 ? "Adesso" : `In corso da ${formatDuration(elapsedMinutes)}`,
    };
  }

  const nextToday = scheduled.find(
    (item) =>
      item.startMs > nowMs &&
      toTimeZoneDateKey(item.appointment.start_time, timeZone) === todayKey
  );
  if (nextToday) {
    const minutesUntil = Math.max(1, Math.ceil((nextToday.startMs - nowMs) / 60000));
    return {
      kind: "next",
      appointment: nextToday.appointment,
      minutesUntil,
      timingLabel: minutesUntil <= 5 ? "Prossimo appuntamento" : `Tra ${formatDuration(minutesUntil)}`,
    };
  }

  const nextFuture = scheduled.find((item) => item.startMs > nowMs);
  if (nextFuture) {
    return {
      kind: "future",
      appointment: nextFuture.appointment,
      minutesUntil: null,
      timingLabel: null,
    };
  }

  return { kind: "empty", appointment: null, minutesUntil: null, timingLabel: null };
};

export const formatFutureAppointmentLabel = (
  customerName,
  startTime,
  timeZone = DEFAULT_BUSINESS_TIMEZONE
) => {
  const date = new Date(startTime);
  if (Number.isNaN(date.getTime())) return customerName || "Cliente";
  const dateLabel = new Intl.DateTimeFormat("it-IT", {
    timeZone,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(date);
  const timeLabel = new Intl.DateTimeFormat("it-IT", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  return `${customerName || "Cliente"} · ${dateLabel} alle ${timeLabel}`;
};
